import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../lib/adminApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { Plus, Trash2, RotateCcw, Save } from 'lucide-react';

const selectCls = 'h-9 px-2 bg-slate-950 border border-white/10 text-white rounded-lg text-sm focus:outline-none';
const inputCls = 'h-9 bg-slate-950 border-white/10 text-white rounded-lg text-sm';

const RuleRows = ({ rules, partners, onChange, testId }) => {
  const update = (i, k, v) => onChange(rules.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid={testId}>
      {rules.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_70px_2fr_36px] gap-2 items-center">
          <select value={r.partner} onChange={(e) => update(i, 'partner', e.target.value)} className={selectCls} data-testid="rule-partner-select">
            {partners.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <Input type="number" min="0" max="20" value={r.puntos} onChange={(e) => update(i, 'puntos', Number(e.target.value))} className={inputCls} data-testid="rule-puntos-input" />
          <Input value={r.razon} onChange={(e) => update(i, 'razon', e.target.value)} className={inputCls} placeholder="Razón que verá el visitante" data-testid="rule-razon-input" />
          <button type="button" onClick={() => onChange(rules.filter((_, idx) => idx !== i))} className="text-red-300 hover:text-red-200" aria-label="Eliminar regla" data-testid="rule-delete-btn"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rules, { partner: partners[0], puntos: 1, razon: '' }])} className="text-xs text-blue-300 hover:text-blue-200 inline-flex items-center" data-testid="rule-add-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Agregar regla</button>
    </div>
  );
};

export const RulesEditor = ({ onAuthError }) => {
  const { toast } = useToast();
  const [meta, setMeta] = useState(null);
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => adminApi.get('/comparador/rules').then(({ data }) => { setMeta(data); setRules(data.data); }).catch(onAuthError), [onAuthError]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put('/comparador/rules', rules);
      toast({ title: 'Reglas guardadas', description: 'El comparador ya usa las nuevas reglas.' });
    } catch (err) {
      if (!onAuthError(err)) toast({ title: 'Error', description: err.response?.data?.detail || 'Revisa que todas las razones tengan texto.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('¿Restaurar las reglas por defecto? Se perderán tus cambios.')) return;
    await adminApi.post('/comparador/rules/reset').catch(onAuthError);
    toast({ title: 'Reglas restauradas' });
    load();
  };

  const toggleExcluido = (destino, partner) => setRules((r) => {
    const cur = r.excluidos[destino] || [];
    return { ...r, excluidos: { ...r.excluidos, [destino]: cur.includes(partner) ? cur.filter((p) => p !== partner) : [...cur, partner] } };
  });

  if (!rules || !meta) return <section className="bg-white/5 border border-white/10 rounded-3xl p-6 text-slate-400" data-testid="admin-rules-loading">Cargando reglas...</section>;

  return (
    <section className="bg-white/5 border border-white/10 rounded-3xl p-6 space-y-8" data-testid="admin-rules-editor">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Reglas del comparador</h2>
          <p className="text-slate-400 text-sm">Cada regla suma puntos a un aliado. El de mayor puntaje se recomienda primero.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} className="rounded-xl border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="rules-reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Restaurar por defecto</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-blue-600 hover:bg-blue-500" data-testid="rules-save-btn"><Save className="h-4 w-4 mr-2" /> {saving ? 'Guardando...' : 'Guardar reglas'}</Button>
        </div>
      </div>

      <div className="space-y-6">
        <h3 className="text-sm uppercase tracking-wider text-slate-400">Por destino</h3>
        {Object.entries(meta.destinos).map(([key, label]) => (
          <div key={key} className="bg-slate-900/50 rounded-2xl p-5 space-y-4" data-testid={`rules-destino-${key}`}>
            <h4 className="font-semibold">{label}</h4>
            <RuleRows rules={rules.destinos[key] || []} partners={meta.partners} onChange={(v) => setRules((r) => ({ ...r, destinos: { ...r.destinos, [key]: v } }))} />
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-400">Excluir para este destino:</span>
              {meta.partners.map((p) => (
                <label key={p} className="inline-flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1 cursor-pointer">
                  <input type="checkbox" checked={(rules.excluidos[key] || []).includes(p)} onChange={() => toggleExcluido(key, p)} data-testid={`rules-excluir-${key}-${p.split(' ')[0].toLowerCase()}`} /> {p}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <h3 className="text-sm uppercase tracking-wider text-slate-400">Por prioridad del viajero</h3>
        {Object.entries(meta.prioridades).map(([key, label]) => (
          <div key={key} className="bg-slate-900/50 rounded-2xl p-5 space-y-4" data-testid={`rules-prioridad-${key}`}>
            <h4 className="font-semibold">{label}</h4>
            <RuleRows rules={rules.prioridades[key] || []} partners={meta.partners} onChange={(v) => setRules((r) => ({ ...r, prioridades: { ...r.prioridades, [key]: v } }))} />
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-slate-900/50 rounded-2xl p-5 space-y-4" data-testid="rules-edad">
          <div className="flex items-center gap-3"><h4 className="font-semibold">Edad mayor a</h4><Input type="number" min="0" max="99" value={rules.edad_umbral} onChange={(e) => setRules((r) => ({ ...r, edad_umbral: Number(e.target.value) }))} className={`${inputCls} w-20`} data-testid="rules-edad-umbral" /><span className="text-sm text-slate-400">años</span></div>
          <RuleRows rules={rules.edad_reglas} partners={meta.partners} onChange={(v) => setRules((r) => ({ ...r, edad_reglas: v }))} />
        </div>
        <div className="bg-slate-900/50 rounded-2xl p-5 space-y-4" data-testid="rules-dias">
          <div className="flex items-center gap-3"><h4 className="font-semibold">Viaje mayor a</h4><Input type="number" min="1" max="365" value={rules.dias_umbral} onChange={(e) => setRules((r) => ({ ...r, dias_umbral: Number(e.target.value) }))} className={`${inputCls} w-20`} data-testid="rules-dias-umbral" /><span className="text-sm text-slate-400">días</span></div>
          <RuleRows rules={rules.dias_reglas} partners={meta.partners} onChange={(v) => setRules((r) => ({ ...r, dias_reglas: v }))} />
        </div>
      </div>
    </section>
  );
};

export default RulesEditor;
